import React, { useState, useEffect } from 'react';
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarTodayIcon,
  DateRange as DateRangeIcon
} from '@mui/icons-material';
import WidgetWrapper from './WidgetWrapper';
import axiosInstance from '../../../services/axiosConfig';

// Default number format: Turkish style (dot as thousands, comma as decimal)
const DEFAULT_NUMBER_FORMAT = {
  decimals: 2,
  decimalSeparator: ',',
  thousandsSeparator: '.'
};

// Helper function to format numbers with decimal and thousands separators
const formatNumber = (value, format) => {
  if (value === null || value === undefined) return value;
  const numValue = Number(value);
  if (isNaN(numValue)) return value;

  // Use provided format or default format
  const effectiveFormat = format || DEFAULT_NUMBER_FORMAT;
  const { decimals = 2, decimalSeparator = ',', thousandsSeparator = '.' } = effectiveFormat;

  const rounded = Number(numValue.toFixed(decimals));
  const [intPart, decPart] = rounded.toFixed(decimals).split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

  return decPart ? `${formattedInt}${decimalSeparator}${decPart}` : formattedInt;
};

const TextBox = ({
  value = '',
  placeholder = 'Enter text...',
  readOnly = false,
  tagId = null,
  tagValue = null, // Will be injected by preview mode
  unit = '', // Unit suffix
  numberFormat = null, // { decimals, decimalSeparator, thousandsSeparator }
  onChange,
  // Style properties
  backgroundColor,
  foreColor,
  borderWidth,
  borderColor,
  borderRadius,
  fontSize,
  fontWeight,
  fontFamily,
  opacity,
  ...props
}) => {
  // Use tag value if available, otherwise use static value
  const [localValue, setLocalValue] = useState(tagValue !== null && tagValue !== undefined ? tagValue : value);

  // Tag info dialog state
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [tagInfo, setTagInfo] = useState(null);

  useEffect(() => {
    const currentValue = tagValue !== null && tagValue !== undefined ? tagValue : value;
    // Format numeric values for display (use provided format or default Turkish format)
    // Only format if it's a number
    const numValue = Number(currentValue);
    const isNumeric = currentValue !== '' && currentValue !== null && currentValue !== undefined && !isNaN(numValue);
    const displayVal = isNumeric ? formatNumber(currentValue, numberFormat) : currentValue;
    setLocalValue(displayVal);
  }, [tagValue, value, numberFormat]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  // Context menu handler - opens tag info dialog
  const handleContextMenu = async (event) => {
    // Only show dialog if widget has a tag
    if (tagId) {
      event.preventDefault();
      event.stopPropagation();

      // Fetch tag details from API
      try {
        const response = await axiosInstance.get(`/Tags/${tagId}`);
        const data = response.data;

        setTagInfo({
          tagId: data.tagId || tagId,
          tagName: data.tagName || data.TagName || 'Unknown',
          tagName2: data.tagName2 || data.TagName2 || '',
          currentValue: localValue, // Use localValue instead of tagValue
          unit: unit || ''
        });
        setTagDialogOpen(true);
      } catch (error) {
        console.error('Failed to fetch tag info:', error);
        // Show dialog with basic info even if API fails
        setTagInfo({
          tagId: tagId,
          tagName: 'Unknown',
          tagName2: '',
          currentValue: localValue, // Use localValue instead of tagValue
          unit: unit || ''
        });
        setTagDialogOpen(true);
      }
    }
  };

  const handleCloseDialog = () => {
    setTagDialogOpen(false);
  };

  // Generate trend report URLs
  const getTrendUrl = (period) => {
    if (!tagId) return '#';

    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);

    let startDate, endDate;

    switch (period) {
      case 'daily':
        // Yesterday start to yesterday end
        startDate = new Date(yesterday.setHours(0, 0, 0, 0));
        endDate = new Date(yesterday.setHours(23, 59, 59, 999));
        break;
      case 'monthly':
        // First day of current month to yesterday end
        startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        endDate = new Date(yesterday.setHours(23, 59, 59, 999));
        break;
      case 'yearly':
        // First day of year to yesterday end
        startDate = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
        endDate = new Date(yesterday.setHours(23, 59, 59, 999));
        break;
      default:
        return '#';
    }

    const formatDate = (date) => date.toISOString().split('T')[0];

    return `/raporlama?tagId=${tagId}&startDate=${formatDate(startDate)}&endDate=${formatDate(endDate)}&period=${period}`;
  };

  return (
    <WidgetWrapper 
      backgroundColor={backgroundColor}
      foreColor={foreColor}
      borderWidth={borderWidth}
      borderColor={borderColor}
      borderRadius={borderRadius}
      fontSize={fontSize}
      fontWeight={fontWeight}
      fontFamily={fontFamily}
      opacity={opacity}
      {...props}
    >
      <TextField
        value={localValue}
        placeholder={placeholder}
        onChange={handleChange}
        onContextMenu={handleContextMenu}
        disabled={readOnly}
        variant="outlined"
        size="small"
        fullWidth
        title={tagId ? 'Sağ tıklayarak tag seçeneklerini görüntüleyin' : undefined}
        inputProps={{
          style: {
            fontSize: fontSize ? `${fontSize}px` : 'inherit',
            padding: '0px 8px',
            lineHeight: '1',
            margin: 0,
            boxSizing: 'border-box',
            textAlign: 'center'
          }
        }}
        sx={{
          height: '100%',
          margin: 0,
          '& .MuiOutlinedInput-root': {
            height: '100%',
            backgroundColor: 'transparent',
            fontSize: fontSize ? `${fontSize}px` : 'inherit',
            display: 'flex',
            alignItems: 'center',
            padding: 0,
            margin: 0,
            '& input': {
              color: 'inherit',
              fontSize: fontSize ? `${fontSize}px` : 'inherit',
              fontWeight: 'inherit',
              padding: '0 8px',
              margin: 0,
              lineHeight: '1',
              height: 'auto',
              boxSizing: 'border-box',
              textAlign: 'center'
            },
            '& fieldset': {
              fontWeight: 'normal',
              top: 0,
              '& legend': {
                display: 'none'
              }
            }
          }
        }}
      />

      {/* Tag Info Dialog */}
      <Dialog
        open={tagDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="xs"
        PaperProps={{
          sx: {
            bgcolor: '#1e1e1e',
            color: 'white',
            minWidth: 350
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#2a2a2a', color: 'white', py: 1.5 }}>
          Tag Bilgileri
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#1e1e1e' }}>
          {tagInfo && (
            <Box sx={{ py: 2 }}>
              {/* Tag Details */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#888' }}>
                  Tag ID
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1.5, fontSize: '0.95rem' }}>
                  {tagInfo.tagId}
                </Typography>

                <Typography variant="caption" sx={{ color: '#888' }}>
                  Tag Adı
                </Typography>
                <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1.5, fontSize: '0.95rem' }}>
                  {tagInfo.tagName}
                </Typography>

                {tagInfo.tagName2 && (
                  <>
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      Tag Adı 2
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1.5, fontSize: '0.95rem' }}>
                      {tagInfo.tagName2}
                    </Typography>
                  </>
                )}

                <Typography variant="caption" sx={{ color: '#888' }}>
                  Güncel Değer
                </Typography>
                <Typography variant="h6" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                  {tagInfo.currentValue !== null && tagInfo.currentValue !== undefined
                    ? `${tagInfo.currentValue} ${tagInfo.unit}`
                    : 'N/A'}
                </Typography>
              </Box>

              <Divider sx={{ my: 2, borderColor: '#444' }} />

              {/* Trend Reports */}
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold', color: '#fff' }}>
                Trend Raporları
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CalendarTodayIcon />}
                  fullWidth
                  component="a"
                  href={getTrendUrl('daily')}
                  onClick={handleCloseDialog}
                  sx={{
                    color: '#fff',
                    borderColor: '#555',
                    '&:hover': { borderColor: '#888', bgcolor: '#333' }
                  }}
                >
                  Günlük Trend (Dün)
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DateRangeIcon />}
                  fullWidth
                  component="a"
                  href={getTrendUrl('monthly')}
                  onClick={handleCloseDialog}
                  sx={{
                    color: '#fff',
                    borderColor: '#555',
                    '&:hover': { borderColor: '#888', bgcolor: '#333' }
                  }}
                >
                  Aylık Trend (Ay Başı - Dün)
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TrendingUpIcon />}
                  fullWidth
                  component="a"
                  href={getTrendUrl('yearly')}
                  onClick={handleCloseDialog}
                  sx={{
                    color: '#fff',
                    borderColor: '#555',
                    '&:hover': { borderColor: '#888', bgcolor: '#333' }
                  }}
                >
                  Yıllık Trend (Yıl Başı - Dün)
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#2a2a2a', px: 2, py: 1.5 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{ color: '#fff' }}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </WidgetWrapper>
  );
};

export default TextBox;